import { Dispatch, SetStateAction, useState } from 'react';
import styled, { css } from 'styled-components';
import { darken } from 'polished';
import { IoCloseOutline } from 'react-icons/io5';

import theme from '@styles/theme';

import { RegularFont, FlexBox } from '@components/styled/StyledComponents';
import SearchModal from '@components/share/Modal/SearchModal';

type HeadlineProps = {
    SEARCH?: boolean; // 검색 사용
    title: string;
    subTitle?: string;
    searchCount?: string | number;
    searchTerm?: string;
    setSearchTerm?: Dispatch<SetStateAction<string>>; // 검색 기능 처리
    fontColor?: string;
    buttonList?: string[];
    buttonAction?: (() => void)[];
    DETAIL?: boolean;
};

const Headline = ({
    SEARCH,
    title,
    subTitle,
    searchCount,
    searchTerm,
    setSearchTerm = () => null,
    fontColor = theme.colors.deepGrayFontColor,
    buttonList,
    buttonAction,
    DETAIL
}: HeadlineProps) => {
    const [searchModal, setSearchModal] = useState(false);

    return (
        <Wrapper>
            <Title $marginBottom={subTitle ? 12 : 30}>{title}</Title>
            <FlexBox
                $justifyContent="space-between"
                $alignItems="center"
                $height="auto"
                $margin={DETAIL ? '0 0 32px' : '0 0 20px'}
            >
                <SubTitle>{subTitle}</SubTitle>
                <ButtonBox>
                    {/* 버튼 리스트 */}
                    {SEARCH && (
                        <Button onClick={() => setSearchModal(true)}>
                            검색
                        </Button>
                    )}
                    {buttonList?.map((button, index) => {
                        if (!button) return;

                        return (
                            <Button
                                key={`${index}-${button}-button`}
                                onClick={buttonAction![index]}
                                $fontColor={fontColor}
                                $delete={button.includes('삭제')}
                            >
                                {button}
                            </Button>
                        );
                    })}
                </ButtonBox>
            </FlexBox>
            <SearchBox $active={!!searchTerm}>
                {searchTerm && (
                    <>
                        <SearchTerm>
                            {searchTerm}
                            <DeleteIcon onClick={() => setSearchTerm('')}>
                                <IoCloseOutline />
                            </DeleteIcon>
                        </SearchTerm>
                        <CountText>
                            {searchCount?.toLocaleString()}개의 검색결과
                        </CountText>
                    </>
                )}
            </SearchBox>
            {searchModal && (
                <SearchModal
                    searchModal={searchModal}
                    setSearchModal={setSearchModal}
                    setSearchTerm={setSearchTerm}
                />
            )}
        </Wrapper>
    );
};

export default Headline;

const Wrapper = styled.div`
    margin-top: 32px;
    width: 100%;
`;

const Title = styled.h3<{ $marginBottom?: number }>`
    margin-bottom: ${({ $marginBottom }) => $marginBottom}px;
    font-size: 16px;
    font-family: PretendardSemiBold, sans-serif;
`;

const SubTitle = styled(RegularFont)`
    font-size: 14px;
    color: ${theme.colors.deepGrayFontColor};
`;

const ButtonBox = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

const Button = styled.button<{
    $fontColor?: string;
    $delete?: boolean;
}>`
    min-width: 36px;
    font-size: 14px;
    color: ${({ $fontColor }) =>
        $fontColor ? $fontColor : theme.colors.deepGrayFontColor};
    font-family: PretendardRegular, sans-serif;
    transition: 0.25s;

    &:hover {
        color: ${({ $fontColor }) =>
            $fontColor
                ? darken(0.2, $fontColor)
                : darken(0.35, theme.colors.deepGrayFontColor)};
    }

    ${({ $delete }) =>
        $delete &&
        css`
            &:hover {
                color: ${theme.colors.activeOrange};
            }
        `};
`;

const SearchBox = styled.div<{ $active?: boolean }>`
    width: 100%;
    transition: 0.25s;

    ${({ $active }) =>
        $active &&
        css`
            margin: 28px 0;
        `};
`;
const SearchTerm = styled.div`
    display: flex;
    font-size: 16px;
    font-family: PretendardMedium, sans-serif;
`;

const DeleteIcon = styled.div`
    margin-left: 8px;
    cursor: pointer;
`;

const CountText = styled(RegularFont)`
    margin-top: 10px;
    color: ${theme.colors.deepGrayFontColor};
`;
